import { KeyboardEvent, MouseEvent, ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Tooltip } from "@snpjs/core";
import { createColumnHelper, Table, useTable, useTableState } from "@snpjs/table";

import { TableFilterType } from "./FilterComponents/TableFilter.types";
import { BooleanFilterComponent } from "./FilterComponents/BooleanFilterComponent";
import { DateFilterComponent } from "./FilterComponents/DateFilterComponent";
import { MultiSelectFilterComponent } from "./FilterComponents/MultiSelectFilterComponent";
import { NumberFilterComponent } from "./FilterComponents/NumberFilterComponent";
import { TextFilterComponent } from "./FilterComponents/TextFilterComponent";
import { TimestampFilterComponent } from "./FilterComponents/TimestampFilterComponent";
import { TimeFilterComponent } from "./FilterComponents/TimeFilterComponent";
import styles from "./Table2.module.scss";

type TableRow = Record<string, unknown>;
type TableRenderCell = (columnId: string, value: unknown, row: TableRow) => ReactNode | undefined;

export type TableToolbarContent = ReactNode | ((table: any) => ReactNode);

export interface Table2Props {
  data: TableRow[];
  isLoading?: boolean;
  noResultsTitle?: ReactNode;
  onVisibleCountChange?: (count: number) => void;
  renderCell?: TableRenderCell;
  filterTypes?: Record<string, TableFilterType>;
  showUtilityToolbar?: boolean;
  enableGlobalFilter?: boolean;
  leftToolbar?: TableToolbarContent;
  rightToolbar?: TableToolbarContent;
}

const columnHelper = createColumnHelper<TableRow>();
const Table2View = Table.ofType<TableRow>();
const CATEGORY_OPTION_LIMIT = 20;

const ExpandableCell: React.FC<{ children: ReactNode }> = ({ children }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useLayoutEffect(() => {
    const element = contentRef.current;
    if (!element || isExpanded) return;

    const checkOverflow = () => {
      const overflows = element.scrollWidth > element.clientWidth + 1 ||
        element.scrollHeight > element.clientHeight + 1;
      setIsOverflowing(overflows);
    };

    checkOverflow();
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(element);
    return () => observer.disconnect();
  }, [children, isExpanded]);

  const toggleExpanded = (event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest("button, a, input, select, textarea")) return;
    if ("key" in event && event.key !== "Enter" && event.key !== " ") return;
    if ("key" in event) event.preventDefault();
    setIsExpanded((expanded) => !expanded);
  };

  return (
    <div
      ref={contentRef}
      className={isExpanded ? styles.expandedCell : styles.truncatedCell}
      onClick={isOverflowing ? toggleExpanded : undefined}
      onKeyDown={isOverflowing ? toggleExpanded : undefined}
      role={isOverflowing ? "button" : undefined}
      tabIndex={isOverflowing ? 0 : undefined}
      aria-expanded={isOverflowing ? isExpanded : undefined}
    >
      {children}
    </div>
  );
};

const normalizeDateValue = (value: unknown): string => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? "" : value.toISOString().slice(0, 10);
  }

  const rawValue = String(value ?? "").trim();
  let parts: [string, string, string] | null = null;

  if (/^\d{8}$/.test(rawValue)) {
    parts = [rawValue.slice(0, 4), rawValue.slice(4, 6), rawValue.slice(6, 8)];
  } else if (/^\d{4}-\d{2}-\d{2}/.test(rawValue)) {
    parts = [rawValue.slice(0, 4), rawValue.slice(5, 7), rawValue.slice(8, 10)];
  } else if (/^\d{2}\.\d{2}\.\d{4}$/.test(rawValue)) {
    const [day, month, year] = rawValue.split(".");
    parts = [year, month, day];
  }

  if (parts) {
    const [year, month, day] = parts.map(Number);
    const parsed = new Date(Date.UTC(year, month - 1, day));
    if (parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day) {
      return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
    return "";
  }

  return "";
};

const normalizeTimestampValue = (value: unknown): string => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? "" : value.toISOString().slice(0, 19);
  }

  const rawValue = String(value ?? "").trim();
  if (/^\d{14}$/.test(rawValue)) {
    return `${rawValue.slice(0, 4)}-${rawValue.slice(4, 6)}-${rawValue.slice(6, 8)}T${rawValue.slice(8, 10)}:${rawValue.slice(10, 12)}:${rawValue.slice(12, 14)}`;
  }

  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(rawValue)) {
    return rawValue.replace(" ", "T").slice(0, 19);
  }

  return "";
};

const isTimestampValue = (value: unknown): boolean => normalizeTimestampValue(value) !== "";

const normalizeTimeValue = (value: unknown): string => {
  const rawValue = String(value ?? "").trim();
  let parts: [string, string, string] | null = null;

  if (/^\d{6}$/.test(rawValue)) {
    parts = [rawValue.slice(0, 2), rawValue.slice(2, 4), rawValue.slice(4, 6)];
  } else if (/^\d{2}:\d{2}:\d{2}$/.test(rawValue)) {
    parts = rawValue.split(":") as [string, string, string];
  }

  if (!parts) return "";
  const [hours, minutes, seconds] = parts.map(Number);
  if (hours > 23 || minutes > 59 || seconds > 59) return "";
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const isTimeColumn = (columnId: string): boolean => /(?:^|[_\s])time(?:$|[_\s])/i.test(columnId);

const isAbapBooleanColumn = (values: unknown[]): boolean =>
  values.some((value) => typeof value === "string" && value.toUpperCase() === "X") &&
  values.every((value) => value === null || value === undefined || value === "" ||
    (typeof value === "string" && ["X", "TRUE", "FALSE"].includes(value.trim().toUpperCase())));

const inferFilterType = (columnId: string, values: unknown[]): TableFilterType => {
  const presentValues = values.filter((value) => value !== null && value !== undefined && value !== "");
  if (isAbapBooleanColumn(values) || (presentValues.length > 0 && presentValues.every((value) => typeof value === "boolean"))) return "boolean";
  if (presentValues.length > 0 && presentValues.every((value) => typeof value === "number")) return "number";
  if (presentValues.length > 0 && presentValues.every(isTimestampValue)) return "timestamp";
  if (isTimeColumn(columnId) && presentValues.length > 0 && presentValues.every((value) => normalizeTimeValue(value) !== "")) return "time";
  if (presentValues.length > 0 && presentValues.every((value) => normalizeDateValue(value) !== "")) return "date";

  const distinctValues = new Set(presentValues.map(String));
  if (distinctValues.size > 1 && distinctValues.size <= CATEGORY_OPTION_LIMIT) return "select";
  return "text";
};

const getFilterOptions = (values: unknown[]): string[] =>
  Array.from(new Set(values
    .filter((value) => value !== null && value !== undefined && value !== "")
    .map(String)))
    .sort((left, right) => left.localeCompare(right));

const isTrueValue = (value: unknown): boolean =>
  value === true || (typeof value === "string" && ["X", "TRUE", "1"].includes(value.trim().toUpperCase()));

export const Table2: React.FC<Table2Props> = ({
  data,
  isLoading,
  noResultsTitle,
  onVisibleCountChange,
  renderCell,
  filterTypes,
  showUtilityToolbar = true,
  enableGlobalFilter = true,
  leftToolbar,
  rightToolbar,
}) => {
  const columns = useMemo(() => {
    const columnIds = Array.from(new Set(data.flatMap((row) => Object.keys(row))));

    return columnIds.map((columnId) => {
      const values = data.map((row) => row[columnId]);
      const options = getFilterOptions(values);
      const filterType = filterTypes?.[columnId] ?? inferFilterType(columnId, values);

      return columnHelper.accessor((row) => row[columnId], {
        id: columnId,
        header: columnId,
        enableColumnFilter: true,
        filterFn: (row, id, filterValue: unknown) => {
          const cellValue = row.getValue(id);
          if (filterValue === undefined || filterValue === null || filterValue === "") return true;

          switch (filterType) {
            case "number": {
              const [minimum, maximum] = filterValue as [number | null, number | null];
              const numericValue = Number(cellValue);
              return Number.isFinite(numericValue) &&
                (minimum === null || numericValue >= minimum) &&
                (maximum === null || numericValue <= maximum);
            }
            case "date": {
              const [start, end] = filterValue as [string, string];
              const dateValue = normalizeDateValue(cellValue);
              return dateValue !== "" && (!start || dateValue >= start) && (!end || dateValue <= end);
            }
            case "timestamp": {
              const [start, end] = filterValue as [string, string];
              const timestampValue = normalizeTimestampValue(cellValue);
              return timestampValue !== "" && (!start || timestampValue >= start) && (!end || timestampValue <= end);
            }
            case "time": {
              const [start, end] = filterValue as [string, string];
              const timeValue = normalizeTimeValue(cellValue);
              const startTime = start ? normalizeTimeValue(start) : "";
              const endTime = end ? normalizeTimeValue(end) : "";
              return timeValue !== "" && (!startTime || timeValue >= startTime) && (!endTime || timeValue <= endTime);
            }
            case "boolean": {
              const booleanFilter = filterValue as { isTrue: boolean; isFalse: boolean };
              const actualIsTrue = isTrueValue(cellValue);
              return actualIsTrue ? booleanFilter.isTrue : booleanFilter.isFalse;
            }
            case "select":
            case "multiSelect":
              return Array.isArray(filterValue)
                ? filterValue.includes(String(cellValue ?? ""))
                : String(cellValue ?? "") === String(filterValue);
            case "text":
            default:
              return String(cellValue ?? "").toLowerCase().includes(String(filterValue).toLowerCase());
          }
        },
        filter: ({ column }) => {
          const filterProps = {
            value: column.getFilterValue(),
            options,
            onChange: column.setFilterValue,
          };

          switch (filterType) {
            case "number": return <NumberFilterComponent {...filterProps} />;
            case "date": return <DateFilterComponent {...filterProps} />;
            case "timestamp": return <TimestampFilterComponent {...filterProps} />;
            case "time": return <TimeFilterComponent {...filterProps} />;
            case "boolean": return <BooleanFilterComponent {...filterProps} />;
            case "select":
            case "multiSelect": return <MultiSelectFilterComponent {...filterProps} />;
            case "text":
            default: return <TextFilterComponent {...filterProps} />;
          }
        },
        cell: (info) => {
          const value = info.getValue();
          const customCell = renderCell?.(columnId, value, info.row.original);
          const cellContent = (
            <ExpandableCell>
              {customCell ?? (value === null || value === undefined ? "" : String(value))}
            </ExpandableCell>
          );
          const isTextColumn = ["text", "select", "multiSelect"].includes(filterType);

          return isTextColumn ? (
            <Tooltip
              className={styles.textTooltip}
              content={<div className={styles.tooltipContent}>{String(value ?? "")}</div>}
              usePortal={true}
            >
              {cellContent}
            </Tooltip>
          ) : cellContent;
        },
      });
    });
  }, [data, filterTypes, renderCell]);

  const table = useTable({
    columns,
    data,
    enableFilters: true,
    enableGlobalFilter,
    isLoading,
    initialState: {
      pagination: { pageSize: 16 },
    },
  });
  const [tableState] = useTableState(table);

  useEffect(() => {
    onVisibleCountChange?.(table.getFilteredRowModel().rows.length);
  }, [data, onVisibleCountChange, table, tableState.columnFilters, tableState.globalFilter]);

  const resolvedLeftToolbar = typeof leftToolbar === "function" ? (leftToolbar as any)(table) : leftToolbar;
  const resolvedRightToolbar = typeof rightToolbar === "function" ? (rightToolbar as any)(table) : rightToolbar;

  return (
    <div className={styles.container}>
      <Table2View
        table={table}
        showUtilityToolbar={showUtilityToolbar}
        leftToolbar={resolvedLeftToolbar as any}
        rightToolbar={resolvedRightToolbar as any}
        noResultsTitle={noResultsTitle}
      />
    </div>
  );
};